import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { PCMP_DISBURSE_VERIFY_DEFAULT_TAB } from '../constants/pcmpDisburseVerify.constants';
import type {
  PcmpDisburseVerifyQueueParams,
  PcmpDisburseVerifySearch,
  PcmpDisburseVerifyTab,
} from '../types/pcmpDisburseVerify.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/disburse-verify/');

export function usePcmpDisburseVerifyFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpDisburseVerifySearch) => PcmpDisburseVerifySearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_DISBURSE_VERIFY_DEFAULT_TAB;

  const queueParams = useMemo<PcmpDisburseVerifyQueueParams>(
    () => ({ ...appliedFilters, tab, page, limit }),
    [appliedFilters, tab, page, limit],
  );

  const setTab = useCallback(
    (next: PcmpDisburseVerifyTab) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_DISBURSE_VERIFY_DEFAULT_TAB ? undefined : next,
        page: undefined,
      }));
    },
    [setSearch],
  );

  return { ...queue, tab, queueParams, setTab };
}

export type PcmpDisburseVerifyFilterController = ReturnType<typeof usePcmpDisburseVerifyFilters>;
